import { Suspense, lazy } from 'react';
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  type RouteObject,
} from 'react-router-dom';
import { RouteErrorBoundary } from '@core/app-update/RouteErrorBoundary';
import { AuthGuard, GuestGuard } from './guards';
import { adminRoutes } from '@modules/admin/routes';
import { widgetRoutes } from '@modules/widget/routes';

const LoginPage = lazy(() => import('@features/auth/components/LoginPage'));
const SignupPage = lazy(() => import('@features/auth/components/SignupPage'));
const SignupConfirmPage = lazy(
  () => import('@features/auth/components/SignupConfirmPage'),
);
const AcceptInvitePage = lazy(
  () => import('@features/auth/components/AcceptInvitePage'),
);
const ForgotPasswordPage = lazy(
  () => import('@features/auth/components/ForgotPasswordPage'),
);
const ResetPasswordPage = lazy(
  () => import('@features/auth/components/ResetPasswordPage'),
);
const GoogleOAuthCallbackPage = lazy(
  () => import('@features/agents/components/GoogleOAuthCallbackPage'),
);
const HubSpotOAuthCallbackPage = lazy(
  () => import('@features/integrations/components/HubSpotOAuthCallbackPage'),
);

function NotFoundPage() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted">
        <span className="text-3xl font-bold text-muted-foreground">404</span>
      </div>
      <div className="text-center">
        <p className="text-lg font-semibold">Page not found</p>
        <p className="mt-1 text-sm text-muted-foreground">
          The page you're looking for doesn't exist.
        </p>
      </div>
      <a
        href="/admin"
        className="mt-2 text-sm font-medium text-primary underline-offset-2 hover:underline"
      >
        Back to dashboard
      </a>
    </div>
  );
}

// ── Route-transition loading fallback ─────────────────────────────────────────
//
// Shown while a lazy-loaded chunk is downloading (only on slow connections or
// first visit — subsequent navigations use cached chunks).

function RouteLoadingFallback() {
  return (
    <div
      className="flex h-screen items-center justify-center"
      style={{ backgroundColor: 'var(--color-background)' }}
      aria-label="Loading page…"
      role="status"
    >
      <div className="flex flex-col items-center gap-3">
        {/* Animated brand mark */}
        <div className="relative">
          <div className="h-10 w-10 rounded-xl bg-primary/20 animate-pulse" />
          <div className="absolute inset-1.5 rounded-lg bg-primary animate-pulse" style={{ animationDelay: '150ms' }} />
        </div>
        <p className="text-xs font-medium text-muted-foreground tracking-wide animate-pulse">
          Loading…
        </p>
      </div>
    </div>
  );
}

// ── Router ────────────────────────────────────────────────────────────────────

const routes: RouteObject[] = [
  // ─── Login (unauthenticated only) ─────────────────────────────────────────
  {
    element: <GuestGuard />,
    children: [{ path: '/login', element: <LoginPage /> }],
  },

  // ─── Public self-service signup ────────────────────────────────────────────
  { path: '/signup', element: <SignupPage /> },
  { path: '/signup/confirm', element: <SignupConfirmPage /> },

  // ─── Public invite / password reset (works logged-in or out) ───────────────
  { path: '/accept-invite', element: <AcceptInvitePage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  // Google Calendar OAuth popup lands here → notifies opener + closes itself.
  { path: '/oauth/google/callback', element: <GoogleOAuthCallbackPage /> },
  { path: '/oauth/hubspot/callback', element: <HubSpotOAuthCallbackPage /> },

  // ─── Admin panel (requires authentication) ────────────────────────────────
  {
    element: <AuthGuard />,
    children: adminRoutes,
  },

  // ─── Public embeddable widget (no auth required) ──────────────────────────
  ...widgetRoutes,

  // ─── Redirects ─────────────────────────────────────────────────────────────
  // Authed users are routed by GuestGuard / login; unauthenticated hit login.
  { index: true, element: <Navigate to="/login" replace /> },
  { path: '*',   element: <NotFoundPage /> },
];

// One error screen for every route: a page file missing after a deploy
// reloads into the new build instead of React Router's developer screen.
const router = createBrowserRouter([
  { errorElement: <RouteErrorBoundary />, children: routes },
]);

export function AppRouter() {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
